/**
 * Sticky horizontal day pill list. Each pill shows `Day N` + short date and a
 * Port/Sea badge. Active day is highlighted. Left/Right arrows navigate ±1.
 */
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function DayNavigator({
  days, activeDay, onSelect, dirty,
  // Phase-5 ref 5 — set of day numbers (Set<number>) that have any
  // duplicate menu rotation. Used to surface a small red dot on the pill.
  daysWithDuplicates,
}) {
  const total = days.length;
  const idx = days.findIndex((d) => d.day_number === activeDay);
  const canPrev = idx > 0;
  const canNext = idx < total - 1;

  const guardedSelect = (n) => {
    if (n === activeDay) return;
    if (dirty && !window.confirm("You have unsaved changes. Discard and switch day?")) return;
    onSelect(n);
  };

  const hasDup = (n) =>
    !!(daysWithDuplicates && daysWithDuplicates.has && daysWithDuplicates.has(n));

  return (
    <div
      className="sticky top-0 z-20 -mx-4 mb-3 flex items-center gap-2 border-b bg-background/95 px-4 py-2 backdrop-blur"
      data-testid="day-navigator"
    >
      <Button
        type="button" variant="ghost" size="icon"
        disabled={!canPrev}
        onClick={() => guardedSelect(days[idx - 1].day_number)}
        data-testid="day-nav-prev"
        aria-label="Previous day"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <div className="flex flex-1 gap-1 overflow-x-auto pb-1">
        {days.map((d) => {
          const isActive = d.day_number === activeDay;
          const dup = hasDup(d.day_number);
          return (
            <button
              key={d.day_number}
              type="button"
              onClick={() => guardedSelect(d.day_number)}
              className={[
                "relative flex shrink-0 flex-col items-center rounded-md border px-3 py-1.5 text-xs transition-colors",
                isActive
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-border bg-card hover:bg-muted",
              ].join(" ")}
              data-testid={`day-nav-${d.day_number}`}
              data-has-duplicates={dup ? "true" : undefined}
              title={dup ? "This day has duplicate menu rotation(s)" : undefined}
              aria-current={isActive ? "true" : undefined}
            >
              {dup && (
                // Small red dot in the top-right corner of the pill.
                <span
                  className="absolute -right-1 -top-1 inline-flex h-2.5 w-2.5 rounded-full border-2 border-background bg-red-500 ring-1 ring-red-300"
                  aria-hidden="true"
                  data-testid={`day-nav-${d.day_number}-dup-dot`}
                />
              )}
              <span className="font-bold leading-tight">Day {d.day_number}</span>
              <span className={isActive ? "opacity-90" : "text-muted-foreground"}>
                {shortDate(d.date)}
              </span>
              <Badge
                variant="outline"
                className={[
                  "mt-1 h-4 px-1 text-[10px] leading-tight",
                  isActive
                    ? "border-primary-foreground/40 bg-transparent text-primary-foreground"
                    : d.port_or_sea === "Port"
                      ? "border-sky-200 bg-sky-50 text-sky-800"
                      : "border-slate-200 bg-slate-50 text-slate-700",
                ].join(" ")}
              >
                {d.port_or_sea === "Port" ? (d.port_name || "Port") : "Sea"}
              </Badge>
            </button>
          );
        })}
      </div>

      <Button
        type="button" variant="ghost" size="icon"
        disabled={!canNext}
        onClick={() => guardedSelect(days[idx + 1].day_number)}
        data-testid="day-nav-next"
        aria-label="Next day"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
